/**
 * v3.21-1737 — SMS Dashboard (two-way SMS operational center).
 *
 * SSoT for inbound + outbound SMS operations.  Reuses the canonical
 * Hub outbound SMS service (POST /api/telephony... no — /api/sms-dashboard/*
 * routes wrapping services/canonical_sms.py).  Deal Ownership drives
 * visibility server-side, so this component never filters rows itself.
 *
 * Layout: left = conversation list with tab filters + search, right =
 * selected conversation thread with Needs-Review candidates,
 * Assign-to-Client, Quick Replies, and freeform composer.
 *
 * v3.21-1737 · The version pill in the page header no longer hard-codes
 * a release string.  It reads from the canonical /api/version endpoint
 * (SYSTEM_VERSION SSoT in backend/main.py + backend/server.py) with a
 * conservative in-build fallback so the label always matches the rest
 * of the Hub.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MessageSquare, Send, Loader2, Search, User, Building2, AlertTriangle,
  CheckCircle2, AlertCircle, RefreshCw, ArrowRight, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import useDocumentTitle from '../hooks/useDocumentTitle';

const FILTER_TABS = [
  { key: 'all',            label: 'All' },
  { key: 'unread',         label: 'Unread' },
  { key: 'needs_review',   label: 'Needs Review' },
  { key: 'matched',        label: 'Matched' },
  { key: 'unknown_sender', label: 'Unknown Senders' },
  { key: 'failed',         label: 'Failed' },
];

const maskPhone = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length >= 4) return `xxx-xxx-${d.slice(-4)}`;
  return v || '—';
};
const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return '—'; }
};
const badgeFor = (state) => {
  if (state === 'matched')        return { className: 'bg-emerald-100 text-emerald-800', label: 'Matched' };
  if (state === 'needs_review')   return { className: 'bg-amber-100 text-amber-800', label: 'Needs Review' };
  if (state === 'unknown_sender') return { className: 'bg-slate-200 text-slate-700', label: 'Unknown' };
  return { className: 'bg-slate-100 text-slate-600', label: state || '—' };
};

// ─── Conversation row ─────────────────────────────────────────────
const ConversationRow = ({ conv, active, onSelect }) => {
  const b = badgeFor(conv.match_state);
  return (
    <button
      type="button"
      data-testid={`sms-conv-row-${conv.id}`}
      onClick={() => onSelect(conv)}
      className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 transition
                  ${active ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}
                  ${conv.unread_count > 0 ? 'bg-amber-50/40' : ''}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${b.className}`}
              data-testid={`sms-conv-state-${conv.id}`}>
          {b.label}
        </span>
        {conv.unread_count > 0 && (
          <span className="inline-flex text-[10px] font-bold text-white bg-rose-600 rounded-full h-4 w-4 items-center justify-center"
                data-testid={`sms-conv-unread-${conv.id}`}>
            {conv.unread_count}
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400">{formatWhen(conv.last_message_at)}</span>
      </div>
      <div className="text-sm font-semibold text-slate-900 truncate">
        <span className="text-slate-500 mr-1">Subject:</span>
        {conv.subject_client_name || <span className="italic text-slate-400">Unassigned</span>}
      </div>
      <div className="text-xs text-slate-600 truncate">
        <span className="text-slate-400 mr-1">Participant:</span>
        {conv.participant_name || '—'}
        {conv.participant_type ? ` · ${conv.participant_type.replace(/_/g, ' ')}` : ''}
        <span className="text-slate-400 mx-1">·</span>
        <span className="font-mono">{maskPhone(conv.participant_phone)}</span>
      </div>
      {conv.credit_application_number && (
        <div className="text-[10px] text-slate-500 mt-0.5">
          CA#: <span className="font-mono">{conv.credit_application_number}</span>
        </div>
      )}
      <div className="text-xs text-slate-500 truncate mt-1">
        {conv.last_message_direction === 'outbound' ? '↑ ' : '↓ '}
        {conv.last_message_preview || '(no preview)'}
      </div>
    </button>
  );
};

// ─── Needs-Review candidate card ──────────────────────────────────
const NeedsReviewCandidates = ({ candidates, onAssign }) => (
  <div className="space-y-2">
    <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
      Candidate conversations (assign one)
    </div>
    {candidates.length === 0 && (
      <div className="text-xs text-slate-500 italic">No structured candidates — search Clients below.</div>
    )}
    {candidates.map((c, idx) => (
      <div key={idx} className="border border-amber-200 bg-amber-50 rounded-md p-2 text-xs space-y-0.5"
           data-testid={`sms-nr-candidate-${idx}`}>
        <div className="font-semibold text-slate-900">{c.subject_client_name || '—'}</div>
        {c.credit_application_number && (
          <div className="text-slate-600">CA#: <span className="font-mono">{c.credit_application_number}</span></div>
        )}
        {c.subject_client_phone && (
          <div className="text-slate-600">Phone: <span className="font-mono">{maskPhone(c.subject_client_phone)}</span></div>
        )}
        {c.approved_amount_expected != null && (
          <div className="text-slate-600">Approved: ${Number(c.approved_amount_expected).toLocaleString()}</div>
        )}
        {c.monthly_payment_expected != null && (
          <div className="text-slate-600">Payment: ${Number(c.monthly_payment_expected).toLocaleString()}/mo</div>
        )}
        <div className="text-slate-500 text-[10px]">Prior outbound: {formatWhen(c.last_message_at)}</div>
        <div className="pt-1">
          <Button size="sm" variant="outline"
                  className="h-6 text-[11px] border-amber-500 text-amber-800 hover:bg-amber-100"
                  data-testid={`sms-nr-assign-candidate-${idx}`}
                  onClick={() => onAssign(c.subject_client_id)}>
            Assign to this Client
          </Button>
        </div>
      </div>
    ))}
  </div>
);

// ─── Assign-to-Client search ──────────────────────────────────────
const AssignClientSearch = ({ onAssigned, conversationId }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const doSearch = useCallback(async () => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    setBusy(true);
    try {
      const { data } = await api.get('/sms-dashboard/clients/search', { params: { q: term, limit: 20 } });
      setResults(data?.clients || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Client search failed');
    } finally { setBusy(false); }
  }, [q]);
  const submit = useCallback(async (clientId, note) => {
    setAssigning(clientId);
    try {
      await api.post(`/sms-dashboard/conversations/${conversationId}/assign`,
                     { client_id: clientId, note });
      toast.success('Conversation assigned');
      onAssigned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally { setAssigning(null); }
  }, [conversationId, onAssigned]);

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        Assign to Client (search Hub directory)
      </div>
      <div className="flex gap-1">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
               placeholder="Client name / Credit App # / phone / email / ID"
               data-testid="sms-nr-client-search-input"
               className="h-8 text-xs" />
        <Button type="button" size="sm" onClick={doSearch} disabled={busy}
                data-testid="sms-nr-client-search-btn"
                className="h-8 gap-1 text-xs">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
          Search
        </Button>
      </div>
      <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
        {results.length === 0 && !busy && (
          <div className="text-[11px] text-slate-500 italic p-2">No matches yet.</div>
        )}
        {results.map((c) => (
          <div key={c.id} className="p-2 text-xs flex items-center justify-between gap-2"
               data-testid={`sms-nr-client-result-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900 truncate">{c.full_name}</div>
              <div className="text-slate-500 text-[10px] truncate">
                {c.credit_application_number && (
                  <>CA#: <span className="font-mono mr-1">{c.credit_application_number}</span></>
                )}
                {c.email && <span className="mr-1">{c.email}</span>}
                {c.phone && <span className="font-mono">{maskPhone(c.phone)}</span>}
              </div>
            </div>
            <Button size="sm" variant="outline"
                    disabled={assigning === c.id}
                    className="h-6 text-[11px]"
                    data-testid={`sms-nr-assign-btn-${c.id}`}
                    onClick={() => submit(c.id)}>
              {assigning === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Assign'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Thread + composer ────────────────────────────────────────────
const ConversationThread = ({ conversation, refresh }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [draft, setDraft] = useState('');
  const [selectedQuick, setSelectedQuick] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!conversation?.id) return;
    setLoading(true);
    try {
      const convRes = await api.get(`/sms-dashboard/conversations/${conversation.id}`);
      setDetail(convRes.data);
      // Mark inbound read on open (server enforces auth).
      if (convRes.data?.conversation?.unread_count > 0) {
        api.post(`/sms-dashboard/conversations/${conversation.id}/read`).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load conversation');
    } finally { setLoading(false); }
  }, [conversation?.id]);

  useEffect(() => {
    // One-shot quick-reply library load (canonical list, rarely changes).
    api.get('/sms-dashboard/quick-replies')
      .then(({ data }) => setQuickReplies(data?.quick_replies || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail?.messages?.length]);

  const conv = detail?.conversation || conversation;
  const isMatched = conv?.match_state === 'matched';
  const isNeedsReview = conv?.match_state === 'needs_review' || conv?.match_state === 'unknown_sender';

  const applyQuick = (qr) => {
    setSelectedQuick(qr.key);
    setDraft(qr.body);
  };

  const sendReply = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    if (!isMatched) {
      toast.error('Assign this conversation to a Client before replying.');
      return;
    }
    setSending(true);
    try {
      const endpoint = selectedQuick
        ? `/sms-dashboard/conversations/${conv.id}/quick-reply`
        : `/sms-dashboard/conversations/${conv.id}/reply`;
      await api.post(endpoint, { body, quick_reply_key: selectedQuick || undefined });
      toast.success('Reply sent');
      setDraft(''); setSelectedQuick(null);
      await load();
      refresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || detail?.error || 'Send failed')
                                            : (typeof detail === 'string' ? detail : 'Send failed');
      toast.error(msg);
    } finally { setSending(false); }
  }, [draft, sending, isMatched, selectedQuick, conv?.id, load, refresh]);

  const handleAssign = useCallback(async (clientId) => {
    try {
      await api.post(`/sms-dashboard/conversations/${conv.id}/assign`, { client_id: clientId });
      toast.success('Conversation assigned');
      await load();
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    }
  }, [conv?.id, load, refresh]);

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
        Select a conversation to open the thread.
      </div>
    );
  }
  if (loading && !detail) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading conversation…
      </div>
    );
  }

  const messages = detail?.messages || [];

  return (
    <div className="h-full flex flex-col" data-testid="sms-conversation-thread">
      {/* Header */}
      <div className="p-3 border-b border-slate-200 bg-white space-y-1">
        <div className="flex items-center gap-2">
          <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${badgeFor(conv?.match_state).className}`}>
            {badgeFor(conv?.match_state).label}
          </span>
          <span className="text-xs text-slate-500">Updated {formatWhen(conv?.updated_at)}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-7"
                  onClick={load} data-testid="sms-thread-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="text-sm font-semibold text-slate-900">
          <span className="text-slate-500 mr-1">Subject:</span>
          {conv?.subject_client_name || <span className="italic text-slate-400">Unassigned</span>}
        </div>
        <div className="text-xs text-slate-600">
          <span className="text-slate-400 mr-1">Participant:</span>
          {conv?.participant_name || '—'}
          {conv?.participant_type ? ` · ${conv.participant_type.replace(/_/g, ' ')}` : ''}
          <span className="mx-1 text-slate-400">·</span>
          <span className="font-mono">{maskPhone(conv?.participant_phone)}</span>
        </div>
        {conv?.credit_application_number && (
          <div className="text-[11px] text-slate-500">
            CA#: <span className="font-mono">{conv.credit_application_number}</span>
          </div>
        )}
      </div>

      {/* Needs-Review resolver */}
      {isNeedsReview && (
        <div className="p-3 border-b border-amber-200 bg-amber-50/50 space-y-3">
          <div className="flex items-start gap-2 text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">This inbound message could not be automatically matched.</div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                Assign the correct Client below — the message is preserved with its original participant identity.
              </div>
            </div>
          </div>
          <NeedsReviewCandidates
            candidates={detail?.needs_review_candidates || []}
            onAssign={handleAssign}
          />
          <AssignClientSearch conversationId={conv?.id} onAssigned={() => { load(); refresh?.(); }} />
        </div>
      )}

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-xs text-slate-400 italic">No messages yet.</div>
        )}
        {messages.map((m) => {
          const inbound = m.direction === 'inbound';
          return (
            <div key={m.id}
                 className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}
                 data-testid={`sms-message-${m.id}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 shadow-sm text-sm whitespace-pre-wrap
                              ${inbound ? 'bg-white border border-slate-200 text-slate-900'
                                        : 'bg-blue-600 text-white'}`}>
                <div>{m.body}</div>
                <div className={`text-[10px] mt-1 ${inbound ? 'text-slate-400' : 'text-blue-100'}`}>
                  {inbound ? '↓ inbound' : '↑ outbound'}
                  <span className="mx-1">·</span>
                  {formatWhen(m.created_at)}
                  {m.status && !inbound && <> <span className="mx-1">·</span>{m.status}</>}
                  {m.status === 'failed' && <XCircle className="inline h-3 w-3 ml-1 text-rose-300" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 bg-white p-3 space-y-2">
        {isMatched ? (
          <>
            <div className="flex flex-wrap gap-1.5">
              {quickReplies.map((qr) => (
                <button
                  key={qr.key}
                  type="button"
                  onClick={() => applyQuick(qr)}
                  data-testid={`sms-quick-reply-${qr.key}`}
                  className={`text-[11px] px-2 py-1 rounded-full border transition
                              ${selectedQuick === qr.key
                                ? 'bg-blue-100 border-blue-500 text-blue-800'
                                : 'bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                >
                  {qr.label}
                </button>
              ))}
            </div>
            <Textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); if (selectedQuick) setSelectedQuick(null); }}
              placeholder="Type a message… (or tap a Quick Reply above; edit if needed before send)"
              rows={2}
              data-testid="sms-composer-textarea"
              className="text-xs"
            />
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400">
                To <span className="font-mono">{maskPhone(conv?.participant_phone)}</span>
                {selectedQuick && <span className="ml-2 text-blue-600">Quick Reply · editable</span>}
              </span>
              <Button
                type="button" size="sm"
                onClick={sendReply}
                disabled={!draft.trim() || sending}
                data-testid="sms-composer-send"
                className="ml-auto h-8 gap-1"
              >
                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                Send
              </Button>
            </div>
          </>
        ) : (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
            Assign this conversation to a Client above before replying.
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Page shell ───────────────────────────────────────────────────
export default function SmsDashboardPage() {
  useDocumentTitle('SMS Dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [badge, setBadge] = useState({ unread: 0, needs_review: 0, unknown_sender: 0, total: 0 });
  const [active, setActive] = useState(null);
  // v3.21-1737 · Canonical current-version source.  Read from the same
  // /api/version endpoint every other Hub screen defers to so the SMS
  // Dashboard never lags behind the sidebar.
  const [hubVersion, setHubVersion] = useState('');
  useEffect(() => {
    api.get('/version')
      .then(({ data }) => setHubVersion(data?.version || ''))
      .catch(() => setHubVersion(''));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, badgeRes] = await Promise.all([
        api.get('/sms-dashboard/conversations', { params: { filter, q: q || undefined, limit: 200 } }),
        api.get('/sms-dashboard/badge').catch(() => ({ data: {} })),
      ]);
      setConversations(listRes.data?.conversations || []);
      setBadge(badgeRes.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'SMS Dashboard failed to load');
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  // Deep-link support: /sms-dashboard?conversation_id=<uuid> and
  // v3.21-1736 · /sms-dashboard?client_id=<uuid> (Client Workspace /
  // Client Detail explicit SMS action).  For a client_id link we fetch
  // that client's conversations from the Deal-Ownership-gated
  // /api/sms-dashboard/clients/{id}/conversations endpoint — one
  // conversation opens directly, multiple render as a client-scoped
  // slice of the main list.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cid = params.get('conversation_id');
    const clientId = params.get('client_id');
    if (cid && (!active || active.id !== cid)) {
      const found = conversations.find((c) => c.id === cid);
      if (found) setActive(found);
      else {
        api.get(`/sms-dashboard/conversations/${cid}`)
          .then(({ data }) => setActive(data?.conversation || null))
          .catch(() => {});
      }
      return;
    }
    if (clientId) {
      api.get(`/sms-dashboard/clients/${clientId}/conversations`)
        .then(({ data }) => {
          const list = data?.conversations || [];
          setConversations(list);
          if (list.length === 1) setActive(list[0]);
        })
        .catch((e) => toast.error(e?.response?.data?.detail || 'No SMS for this Client'));
    }
  }, [location.search, conversations, active]);

  const totalBadge = badge.total || 0;

  return (
    <div className="h-full flex flex-col" data-testid="sms-dashboard-page">
      <div className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-heading font-semibold text-slate-900">SMS Dashboard</h1>
          {totalBadge > 0 && (
            <Badge className="bg-rose-100 text-rose-700 text-[10px]"
                   data-testid="sms-dashboard-header-badge">
              {totalBadge} actionable
            </Badge>
          )}
          <span className="ml-auto text-[10px] text-slate-400" data-testid="sms-dashboard-version-tag">
            {hubVersion || 'v3.21-1754'}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          Inbound &amp; outbound texts — matched conversations follow Deal Ownership.
          Needs Review is the safety net; unmatched messages are never guessed.
        </p>
      </div>

      <div className="border-b border-slate-200 bg-white px-4 py-2 flex items-center gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="sms-dashboard-tabs">
            {FILTER_TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key}
                           data-testid={`sms-dashboard-tab-${t.key}`}>
                {t.label}
                {t.key === 'unread' && badge.unread > 0 && (
                  <span className="ml-1 text-[10px] bg-rose-600 text-white rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">
                    {badge.unread}
                  </span>
                )}
                {t.key === 'needs_review' && badge.needs_review > 0 && (
                  <span className="ml-1 text-[10px] bg-amber-600 text-white rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">
                    {badge.needs_review}
                  </span>
                )}
                {t.key === 'unknown_sender' && badge.unknown_sender > 0 && (
                  <span className="ml-1 text-[10px] bg-slate-500 text-white rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">
                    {badge.unknown_sender}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="ml-auto flex items-center gap-1">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                 placeholder="Search subject or participant…"
                 data-testid="sms-dashboard-search"
                 className="h-8 text-xs w-64" />
          <Button size="sm" variant="outline" onClick={load} className="h-8 gap-1 text-xs"
                  data-testid="sms-dashboard-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex bg-slate-50">
        <div className="w-[420px] border-r border-slate-200 bg-white overflow-y-auto" data-testid="sms-dashboard-list">
          {conversations.length === 0 && !loading && (
            <div className="p-6 text-center text-xs text-slate-400 italic">
              No conversations for this view.
            </div>
          )}
          {conversations.map((c) => (
            <ConversationRow key={c.id} conv={c}
                             active={active?.id === c.id}
                             onSelect={(x) => {
                               setActive(x);
                               const params = new URLSearchParams(location.search);
                               params.set('conversation_id', x.id);
                               navigate({ search: params.toString() }, { replace: true });
                             }} />
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <ConversationThread conversation={active} refresh={load} />
        </div>
      </div>
    </div>
  );
}
